import { environment } from 'environments/environment';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { LogFields } from './log-data.interface';
import { LogLevel } from './log-level.enum';
import { TokenService } from '@app/core/security/token.service';

interface LogEntry {
    level: LogLevel;
    message: string;
    data: LogFields;
    buffer: LogEntry[];
}

enum LoggerEvents {
    Flush = 1
}

export class Logger {
    private readonly APP_FIELD = 'Application';
    private readonly ENV_FIELD = 'Environment';
    private readonly VERSION_FIELD = 'Version';
    private readonly USER_NAME_FIELD = 'UserName';
    private readonly USER_ROLE_FIELD = 'UserRole';
    private readonly ELAPSED_MS_FIELD = 'ElapsedMilliseconds';
    private readonly REQUEST_PATH_FIELD = 'RequestPath';
    private readonly REQUEST_STATUS = 'RequestStatus';
    private readonly URL_FIELD = 'Url';

    private buffer: LogEntry[] = [];
    private internalBuffer: LogEntry[] = [];
    private flush = new Subject<LoggerEvents>();

    constructor(private appName: string, private logEndpoint: string, private tokenService: TokenService) {
        this.flush
            .pipe(
                debounceTime(environment.log.debounceTime),
                filter((event) => event === LoggerEvents.Flush)
            )
            .subscribe(() => this.flushBuffer());
    }

    public log(level: LogLevel, message: string, data: LogFields) {
        if (level >= environment.log.level) {
            if (1 || environment.production) {
                let buffer = (level >= environment.log.sendWithBufferLevel)
                    ? this.internalBuffer.slice().reverse()
                    : [];
                const firstLoggedItemIndex = buffer.findIndex((entry: LogEntry) => (entry.level >= environment.log.level));
                if (firstLoggedItemIndex > -1) {
                    buffer = buffer.slice(0, firstLoggedItemIndex);
                }

                this.buffer.push({
                    level,
                    message,
                    data,
                    buffer
                });
                this.flush.next(LoggerEvents.Flush);
            } else {
                // This is nested to make sure we always end up in here when running locally
                // as in do not && this to the above if...
                // eslint-disable-next-line no-console
                console.log({
                    level,
                    message,
                    data
                });
            }
        }

        if ((environment.log.bufferSize > 0) && (level >= environment.log.storeInBufferLevel)) {
            const newEntry = {
                level,
                message,
                data,
                buffer: []
            };
            const lastEntry = (this.internalBuffer.length > 0) ? this.internalBuffer[this.internalBuffer.length - 1] : null;
            if (!lastEntry || (newEntry.level !== lastEntry.level) || (newEntry.message !== lastEntry.message)) {
                this.internalBuffer.push(newEntry);
                this.internalBuffer.splice(0, this.internalBuffer.length - environment.log.bufferSize);
            }
        }
    }

    private flushBuffer() {
        const data = this.buffer.splice(0);

        if (data.length === 0) {
            return;
        }

        if (this.logEndpoint) {
            const entries = data.map((entry) => this.buildBodyChunk(entry));
            const body = JSON.stringify({messages: entries});

            const xobj = new XMLHttpRequest();
            // eslint-disable-next-line no-console
            xobj.onerror = (err) => console.error(err);
            xobj.open('POST', this.logEndpoint, true);
            xobj.setRequestHeader('Content-Type', 'application/json');

            const token = this.tokenService.getToken();
            const isServerRequest = (this.logEndpoint.indexOf(environment.datahubBaseURI) === 0);
            if (isServerRequest) {
                xobj.setRequestHeader('X-Auth-Token', `${token}`);
            }

            xobj.send(body);
        }
    }

    private buildLogString(entry: LogEntry): string {
        const index = this.buildIndexChunk();
        const body = this.buildBodyChunk(entry);

        // return `${index}\n${body}\n`;
        return `${body}\n`;
    }

    private buildIndexChunk() {
        const date = moment();
        const index = {
            index: {
                _index: `logstash-${date.format('YYYY.M.D')}`,
                _type: 'logevent'
            }
        };

        return JSON.stringify(index);
    }

    private buildBodyChunk(entry: LogEntry) {
        const { level, message, data, buffer } = entry;
        const date = moment();
        const messageTemplate = this.getMessageTemplate();
        const fields = this.getFields(data);
        const bufferList = buffer
            .map((entryItem) => this.buildBodyChunk(entryItem));
        // const bufferStr = buffer
        //     .map((entryItem) => this.buildBodyChunk(entryItem))
        //     .map((entryItemBody) => JSON.stringify(entryItemBody))
        //     .reduce((sum, entryItem) => (sum += entryItem), '');
        const body = {
            'timestamp': `${date.toISOString()}`,
            level: LogLevel[level],
            messageTemplate,
            message,
            fields,
            // buffer: bufferStr
            buffer: bufferList
        };

        return body;
        // return JSON.stringify(body);
    }

    private getMessageTemplate() {
        const fields: string[] = [
            this.APP_FIELD,
            this.ENV_FIELD,
            this.VERSION_FIELD,
            this.USER_NAME_FIELD,
            this.USER_ROLE_FIELD,
            this.ELAPSED_MS_FIELD,
            this.REQUEST_PATH_FIELD,
            this.URL_FIELD
        ];
        const template = fields.map((field) => `{${field}}`).join(' - ');

        return template;
    }

    private getFields(data: LogFields) {
        return {
            [this.APP_FIELD]: this.appName,
            [this.ENV_FIELD]: data.environment,
            [this.VERSION_FIELD]: data.appVersion,
            [this.USER_NAME_FIELD]: data.username,
            [this.USER_ROLE_FIELD]: data.userRole,
            [this.ELAPSED_MS_FIELD]: data.elapsedTime,
            [this.REQUEST_PATH_FIELD]: data.requestPath,
            [this.REQUEST_STATUS]: data.status,
            [this.URL_FIELD]: data.url
        };
    }
}
